import { StopOutlined } from "@ant-design/icons";
import { Spin } from "antd-v5";
import { WidgetErrorFallBack } from "components/widgets/WidgetCard";
import { WidgetEmpty } from "components/widgets/WidgetEmpty";
import { PermissionSetMap } from "constants/RBACConstants";
import useAPIResponse from "hooks/useAPIResponse";
import React, {
  Key,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { hasPermission } from "utils/authService";

import ACPageContainer from "ac-components/ACPageContainer/ACPageContainer";
import ACModalV2 from "ac-components/ac-modal/ACModalV2";
import ACSettingSectionCard from "ac-components/ac-setting-section-card/ACSettingSectionCard";
import ACSettingSectionTitle from "ac-components/ac-setting-section-title/ACSettingSectionTitle";
import ACTabContent from "ac-components/ac-tab-content/ACTabContent";
import ACTabs from "ac-components/ac-tabs/ACTabs";

import AssetCommentSection from "./AssetCommentSection";
import AssetFindingListTable from "./AssetFindingList";
import AssetsFindingListProvider from "./AssetFindingListProvider";
import AssetHistorySection from "./AssetHistorySection";
import AssetOverviewSection from "./AssetOverviewSection";
import AssetTagTableSection from "./AssetTagTableSection";
import AssetsDetailsProvider, {
  useAssetsDetails,
} from "./AssetsDetailsProvider";
import AssetsDetailsTitle from "./AssetsDetailsTitle";
import CorrelatedAssetsSection from "./CorrelatedAssetsSection";

const AssetsDetailsPopup = forwardRef(function AssetsDetailsPopup(
  { assetID }: { assetID: number },
  ref,
) {
  const thisRef = useRef({});

  useImperativeHandle(ref, () => ({ ...thisRef.current }), []);

  return (
    <AssetsDetailsProvider mainModalRef={thisRef} assetID={assetID}>
      <ACModalV2.Title>
        <AssetsDetailsTitle />
      </ACModalV2.Title>
      <div className="asset-modal">
        <div className="asset-modal-body">
          <AssetDetailsModalContent />
        </div>
      </div>
    </AssetsDetailsProvider>
  );
});

const AssetDetailsModalContent = () => {
  const [activeTab, setActiveTab] = useState<Key>("comments");
  const { assetDetails, loading } = useAssetsDetails();
  const [isAssetsCorrelationV2] = useAPIResponse(
    () => GlobalSettingAPI.getAssetCorrelationRuleEngineV2(),
    [],
  );

  return (
    <Spin spinning={loading}>
      <ACPageContainer className="m-0">
        <div className="asset-section-container">
          <AssetOverviewSection assetDetails={assetDetails} />
          {isAssetsCorrelationV2 && (
            <CorrelatedAssetsSection assetDetails={assetDetails} />
          )}
          <AssetTagTableSection assetDetails={assetDetails} />
          <div>
            <ACSettingSectionTitle
              title="Findings"
              description="Findings affected by this asset"
            />
            <ACSettingSectionCard contentPadding={false}>
              {hasPermission(PermissionSetMap.FINDING_READ_DETAILS) ? (
                <AssetsFindingListProvider assetID={assetDetails.id}>
                  <AssetFindingListTable />
                </AssetsFindingListProvider>
              ) : (
                <WidgetEmpty
                  description="No Access"
                  icon={
                    <StopOutlined
                      style={{
                        fontSize: "30px",
                        color: "#979797",
                        marginTop: "44px",
                      }}
                    />
                  }
                  imgPath=""
                />
              )}
            </ACSettingSectionCard>
          </div>
          <div>
            {/* @ts-ignore */}
            <ErrorBoundary FallbackComponent={WidgetErrorFallBack}>
              <ACSettingSectionTitle
                title="Activity"
                description="What’s going on with this asset?"
              />
              <ACSettingSectionCard
                title={
                  <ACTabs
                    responsive
                    value={activeTab}
                    onChange={setActiveTab}
                    size="sm"
                    items={[
                      {
                        label: "Comments",
                        value: "comments",
                      },
                      {
                        label: "History",
                        value: "history",
                      },
                    ]}
                  />
                }
              >
                <ACTabContent
                  value={activeTab}
                  items={[
                    {
                      content: (
                        <AssetCommentSection assetID={assetDetails.id} />
                      ),
                      value: "comments",
                    },
                    {
                      content: (
                        <AssetHistorySection assetID={assetDetails.id} />
                      ),
                      value: "history",
                    },
                  ]}
                />
              </ACSettingSectionCard>
            </ErrorBoundary>
          </div>
        </div>
      </ACPageContainer>
    </Spin>
  );
};

export default AssetsDetailsPopup;
